"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { stack } from "styled-system/patterns";

import { LATEST_SEEN_ANNOUNCEMENT_TIMESTAMP_KEY } from "@/constants";
import { AnnouncementCategory, useLatestAnnouncement } from "@/contexts/queries/announcements";
import { useLocalStorage } from "@/hooks/storage";
import { linkStyles } from "@/recipes/link";

import { HandleSuspenseQuery } from "./handle-query";
import { toast } from "./toast";

export function AnnouncementsNotification() {
  return (
    // No need to show the loading or error state for this background task
    <HandleSuspenseQuery loading={null} error={() => null}>
      <AnnouncementsNotificationContent />
    </HandleSuspenseQuery>
  );
}

const categoryTitles: Record<AnnouncementCategory, string> = {
  community: "community",
  "product-updates": "productUpdates",
  "parameter-updates": "parameterUpdates",
  delistings: "delistedMarkets",
  listings: "newMarkets",
};

function AnnouncementsNotificationContent() {
  const t = useTranslations("announcements");
  const { data: latestAnnouncement } = useLatestAnnouncement();
  const [lastSeen, setLastSeen, isLoading] = useLocalStorage<number>(
    LATEST_SEEN_ANNOUNCEMENT_TIMESTAMP_KEY,
    0,
  );

  useEffect(() => {
    if (isLoading || !latestAnnouncement) return;

    const { timestamp, summary, category } = latestAnnouncement;
    if (timestamp <= lastSeen) return;

    const toastId = "new-announcement";
    // Call toast asynchronously to avoid the following error from React:
    // flushSync was called from inside a lifecycle method. React cannot flush
    // when React is already rendering.
    queueMicrotask(() => {
      toast({
        id: toastId,
        variant: "info",
        title: t(`categoryTitles.${categoryTitles[category]}`),
        description: () => (
          <div className={stack({ gap: "4" })}>
            <p>{summary}</p>
            <Link
              href="/announcements"
              className={linkStyles}
              onClick={() => toast.dismiss(toastId)}
            >
              {t("viewAnnouncements")}
            </Link>
          </div>
        ),
        duration: Infinity,
      });
      setLastSeen(timestamp);
    });
  }, [latestAnnouncement, isLoading, lastSeen, setLastSeen, t]);

  return null;
}
